import { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { useMap } from 'react-map-gl/mapbox';
import { AddCircleIcon, Gps01Icon, MoveIcon, RemoveCircleIcon, RulerIcon, Setting01Icon } from '~/assets/icons';
import styles from '../Map.module.scss';
import { MapLayerOption } from '../hooks/useMapSettings';
import MapSettingsPanel from './MapSettingsPanel';

const cx = classNames.bind(styles);

interface Props {
  layer: MapLayerOption;
  brightness: number;
  onLayerChange: (l: MapLayerOption) => void;
  onBrightnessChange: (v: number) => void;
  isMeasuring: boolean;
  onToggleRuler: () => void;
  onUndoMeasure: () => void;
  onResetMeasure: () => void;
  formattedMeasureDistance: string;
  measurePointCount: number;
  geolocateMaxZoom?: number;
}

interface ControlButton {
  icon: React.ReactNode;
  onClick: () => void;
  title: string;
  active?: boolean;
}

function MapCustomControls({
  layer,
  brightness,
  onLayerChange,
  onBrightnessChange,
  isMeasuring,
  onToggleRuler,
  onUndoMeasure,
  onResetMeasure,
  formattedMeasureDistance,
  measurePointCount,
  geolocateMaxZoom,
}: Props) {
  const { t } = useTranslation('admin/map');
  const { current: map } = useMap();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  // Close settings when clicking outside the controls wrapper
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsSettingsOpen(false);
      }
    };
    if (isSettingsOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSettingsOpen]);

  const handleZoomIn = useCallback(() => map?.zoomIn(), [map]);
  const handleZoomOut = useCallback(() => map?.zoomOut(), [map]);

  const handleGeolocate = useCallback(() => {
    try {
      if (!navigator.geolocation) {
        console.warn('Geolocation is not supported in this browser.');
        alert(t('controls.alerts.geolocationNotSupported'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          try {
            const z = geolocateMaxZoom != null ? geolocateMaxZoom : 15;
            map?.flyTo({
              center: [pos.coords.longitude, pos.coords.latitude],
              zoom: z,
              essential: true,
            });
          } catch (error) {
            console.warn('Unable to move map to current location:', error);
            alert(t('controls.alerts.geolocationMoveFailed'));
          }
        },
        (error) => {
          console.warn('Geolocation error:', error);
          alert(t('controls.alerts.geolocationFetchFailed'));
        }
      );
    } catch (error) {
      console.warn('Geolocation is unavailable:', error);
      alert(t('controls.alerts.geolocationNotSupported'));
    }
  }, [geolocateMaxZoom, map, t]);

  const handleFullscreen = useCallback(async () => {
    try {
      const mapContainer = map?.getMap().getContainer();
      if (!mapContainer) return;

      const fullscreenTarget = (mapContainer.closest('[class*="map-box-body"]') as HTMLElement | null) ?? mapContainer;

      if (typeof fullscreenTarget.requestFullscreen !== 'function') {
        console.warn('Fullscreen is not supported in this browser.');
        alert(t('controls.alerts.fullscreenNotSupported'));
        return;
      }

      if (!document.fullscreenElement) {
        await fullscreenTarget.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.warn('Unable to toggle fullscreen mode:', error);
      alert(t('controls.alerts.fullscreenToggleFailed'));
    }
  }, [map, t]);

  const buttons: ControlButton[] = [
    {
      icon: <Setting01Icon className='w-5 h-5' />,
      onClick: () => setIsSettingsOpen((p) => !p),
      title: t('controls.settings'),
      active: isSettingsOpen,
    },
    { icon: <AddCircleIcon className='w-5 h-5' />, onClick: handleZoomIn, title: t('controls.zoomIn') },
    { icon: <RemoveCircleIcon className='w-5 h-5' />, onClick: handleZoomOut, title: t('controls.zoomOut') },
    { icon: <Gps01Icon className='w-5 h-5' />, onClick: handleGeolocate, title: t('controls.locate') },
    {
      icon: <MoveIcon className='w-5 h-5' />,
      onClick: handleFullscreen,
      title: isFullscreen ? t('controls.exitFullscreen') : t('controls.fullscreen'),
      active: isFullscreen,
    },
    {
      icon: <RulerIcon className='w-5 h-5' />,
      onClick: onToggleRuler,
      title: isMeasuring ? t('controls.stopMeasure') : t('controls.measure'),
      active: isMeasuring,
    },
  ];

  return (
    <div ref={wrapperRef} className={cx('MapCustomControls', 'absolute bottom-0 right-0 z-10')}>
      {isSettingsOpen && (
        <MapSettingsPanel
          layer={layer}
          brightness={brightness}
          onLayerChange={onLayerChange}
          onBrightnessChange={onBrightnessChange}
        />
      )}

      {isMeasuring && (
        <div className={cx('animate-fade-in-up', 'absolute bottom-full right-0 z-30 mb-3')}>
          <div className='flex items-center gap-2 rounded-xl border border-white/70 bg-white/90 px-2.5 py-2 shadow-[0_16px_40px_rgba(15,23,42,0.16)] backdrop-blur-sm'>
            <div className='flex min-w-0 items-center gap-2'>
              <div className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#DBEAFE] text-[#2563EB] [&_svg_path]:stroke-current'>
                <RulerIcon className='h-5 w-5' />
              </div>
              <div className='min-w-0'>
                <p className='whitespace-nowrap text-sm text-[#0F172A]'>
                  {measurePointCount === 0 ? t('controls.measureStart') : t('controls.measuring')}
                  <span className='mx-1 text-[#94A3B8]'>|</span>
                </p>
                <p className='text-[11px] text-[#64748B]'>{t('controls.measureHint')}</p>
              </div>
              <div className='whitespace-nowrap rounded-full bg-[#EFF6FF] px-2.5 py-1 text-sm font-semibold text-[#2563EB]'>
                {formattedMeasureDistance}
              </div>
            </div>

            <div className='ml-1 flex shrink-0 items-center gap-1.5'>
              <button
                type='button'
                onClick={onUndoMeasure}
                disabled={measurePointCount === 0}
                title={t('controls.undoLastPoint')}
                aria-label={t('controls.undoLastPoint')}
                className='flex h-8 w-8 items-center justify-center rounded-lg border border-[#D7DEE7] bg-white text-[#334155] transition hover:border-[#93C5FD] hover:bg-[#F8FAFC] disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:h-4 [&_svg]:w-4 [&_svg_path]:stroke-current'
              >
                <svg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path d='M10 7L5 12L10 17' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
                  <path d='M19 12H6' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
                </svg>
              </button>
              <button
                type='button'
                onClick={onResetMeasure}
                title={t('controls.resetMeasure')}
                aria-label={t('controls.resetMeasure')}
                className='flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-red-600 transition hover:bg-red-100 [&_svg]:h-4 [&_svg]:w-4 [&_svg_path]:stroke-current'
              >
                <svg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path
                    d='M20 12C20 7.58172 16.4183 4 12 4C9.79086 4 7.79086 4.89543 6.34315 6.34315'
                    strokeWidth='1.8'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <path d='M4 4V8H8' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
                  <path
                    d='M4 12C4 16.4183 7.58172 20 12 20C14.2091 20 16.2091 19.1046 17.6569 17.6569'
                    strokeWidth='1.8'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <path d='M20 20V16H16' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Control buttons */}
      <div className={cx('control-buttons', 'flex flex-col rounded-tl-xl p-3 gap-3 bg-white')}>
        {buttons.map((btn) => (
          <button
            key={btn.title}
            onClick={btn.onClick}
            title={btn.title}
            className={[
              'w-8 h-8 flex items-center justify-center transition-colors rounded-[8px] p-[5px] group [&_svg]:w-[18px] [&_svg]:h-[18px] [&_svg_path]:stroke-current [&_svg_circle]:stroke-current [&_svg_line]:stroke-current [&_svg_rect]:stroke-current',
              btn.active ? 'bg-[#0E2F58] text-white' : 'bg-[#F3F6F9] text-gray-600 hover:bg-[#0E2F58] hover:text-white',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <span className={btn.active ? 'text-white' : 'text-gray-600 group-hover:text-white'}>{btn.icon}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default MapCustomControls;
